/* Recurring Transactions Manager — drop-in modal content */
import { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { Plus, Trash2, Zap, RotateCw, Calendar as CalIcon, Loader2 } from "lucide-react";

const fmt = (n) => `${n < 0 ? "-" : ""}$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const CADENCES = [
  { k: "weekly",    en: "Weekly",    es: "Semanal" },
  { k: "biweekly",  en: "Biweekly",  es: "Quincenal" },
  { k: "monthly",   en: "Monthly",   es: "Mensual" },
  { k: "quarterly", en: "Quarterly", es: "Trimestral" },
  { k: "yearly",    en: "Yearly",    es: "Anual" },
];

const RecurringManager = ({ bookId, accounts, lang, onChanged }) => {
  const [recs, setRecs] = useState([]);
  const [busy, setBusy] = useState(false);
  const bankAccounts = useMemo(() => accounts.filter((a) => !a.is_header && a.is_active && (a.subtype === "bank" || a.code.startsWith("10"))), [accounts]);
  const catAccounts = useMemo(() => accounts.filter((a) => !a.is_header && a.is_active && ["income", "cogs", "expense"].includes(a.type)), [accounts]);

  const [form, setForm] = useState({
    name: "", description: "", amount: 0,
    cash_account_code: bankAccounts[0]?.code || "1010",
    category_account_code: catAccounts.find(a => a.type === "expense")?.code || "6900",
    cadence: "monthly",
    start_date: todayISO(),
    end_date: "",
    notes: "",
  });

  const load = async () => {
    const { data } = await api.get(`/back-office/books/${bookId}/recurring`);
    setRecs(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.description) return;
    setBusy(true);
    try {
      await api.post(`/back-office/books/${bookId}/recurring`, {
        ...form,
        amount: Number(form.amount),
        end_date: form.end_date || null,
      });
      toast.success("Recurring scheduled");
      setForm({ ...form, name: "", description: "", amount: 0 });
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const runNow = async (r) => {
    try {
      await api.post(`/back-office/books/${bookId}/recurring/${r.id}/run-now`);
      toast.success("Transaction created");
      load(); onChanged?.();
    } catch { toast.error("Failed"); }
  };

  const processAll = async () => {
    try {
      const { data } = await api.post(`/back-office/books/${bookId}/recurring/process-due`);
      toast.success(`${data.created} transaction(s) created`);
      load(); onChanged?.();
    } catch { toast.error("Failed"); }
  };

  const toggle = async (r) => {
    await api.patch(`/back-office/books/${bookId}/recurring/${r.id}`, { is_active: !r.is_active });
    load();
  };

  const del = async (r) => {
    if (!window.confirm("Delete this recurring schedule?")) return;
    await api.delete(`/back-office/books/${bookId}/recurring/${r.id}`);
    load(); onChanged?.();
  };

  const accLabel = (code) => {
    const a = accounts.find((x) => x.code === code);
    return a ? `${a.code} — ${lang === "es" && a.name_es ? a.name_es : a.name}` : code;
  };

  return (
    <div data-testid="recurring-manager">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-[#737373]">
          {lang === "es" ? "Las transacciones se crean automáticamente en la fecha indicada." : "Transactions auto-post on the scheduled date."}
        </div>
        <button onClick={processAll} className="rn3-btn-gold text-xs" data-testid="rec-process-all">
          <RotateCw className="w-3.5 h-3.5" /> {lang === "es" ? "Procesar Vencidas" : "Process Due"}
        </button>
      </div>

      <form onSubmit={create} className="border border-[#E5E5E5] p-4 bg-[#FDFBF7] space-y-3 mb-6" data-testid="rec-new-form">
        <div className="rn3-eyebrow">{lang === "es" ? "Nuevo Programado" : "New Recurring"}</div>
        <div className="grid grid-cols-12 gap-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={lang === "es" ? "Nombre interno" : "Internal name"} className="col-span-3 rn3-input text-sm" required data-testid="rec-name" />
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={lang === "es" ? "Descripción" : "Description"} className="col-span-3 rn3-input text-sm" required data-testid="rec-desc" />
          <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount" className="col-span-2 rn3-input text-sm" data-testid="rec-amount" />
          <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })} className="col-span-2 rn3-input text-sm" data-testid="rec-cadence">
            {CADENCES.map((c) => <option key={c.k} value={c.k}>{lang === "es" ? c.es : c.en}</option>)}
          </select>
          <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="col-span-2 rn3-input text-sm" required data-testid="rec-start" />
        </div>
        <div className="grid grid-cols-12 gap-2">
          <select value={form.cash_account_code} onChange={(e) => setForm({ ...form, cash_account_code: e.target.value })} className="col-span-5 rn3-input text-sm" data-testid="rec-bank">
            {bankAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
          <select value={form.category_account_code} onChange={(e) => setForm({ ...form, category_account_code: e.target.value })} className="col-span-5 rn3-input text-sm" data-testid="rec-cat">
            {catAccounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
          <button disabled={busy} className="col-span-2 rn3-btn-primary justify-center" data-testid="rec-submit"><Plus className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-[#737373]">
          {lang === "es" ? "Negativo = gasto, positivo = ingreso. Vincula cualquier cuenta del CoA." : "Negative = outflow, positive = inflow. Picks any CoA account."}
        </p>
      </form>

      <div className="space-y-2">
        {recs.length === 0 && <div className="text-sm text-[#737373] text-center py-6"><CalIcon className="w-8 h-8 mx-auto mb-2 text-[#C4A252]" />{lang === "es" ? "Sin programados aún." : "No schedules yet."}</div>}
        {recs.map((r) => (
          <div key={r.id} className={`border ${r.is_active ? "border-[#E5E5E5]" : "border-[#F3EFE9] opacity-60"} bg-white px-4 py-3 grid grid-cols-12 gap-3 items-center`} data-testid={`rec-row-${r.id}`}>
            <div className="col-span-3">
              <div className="font-medium text-sm">{r.name}</div>
              <div className="text-xs text-[#737373]">{r.description}</div>
            </div>
            <div className={`col-span-2 text-sm tabular-nums ${r.amount < 0 ? "text-[#8B1C22]" : "text-[#15803D]"}`}>{fmt(r.amount)}</div>
            <div className="col-span-2 text-xs text-[#737373]">{CADENCES.find(c => c.k === r.cadence)?.[lang]}</div>
            <div className="col-span-2 text-xs">
              <div>{lang === "es" ? "Próximo" : "Next"}: <b>{r.next_run_date}</b></div>
              <div className="text-[#737373]">{r.runs_count || 0} runs</div>
            </div>
            <div className="col-span-3 flex justify-end gap-2 text-xs">
              <button onClick={() => runNow(r)} className="text-[#8B1C22] hover:underline inline-flex items-center gap-1" data-testid={`rec-run-${r.id}`}><Zap className="w-3 h-3" /> Run</button>
              <button onClick={() => toggle(r)} className="text-[#737373] hover:text-[#0A0A0A]" data-testid={`rec-toggle-${r.id}`}>{r.is_active ? "Pause" : "Resume"}</button>
              <button onClick={() => del(r)} className="text-[#737373] hover:text-[#8B1C22]" data-testid={`rec-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className="col-span-12 text-[10px] text-[#737373] uppercase tracking-widest border-t border-[#F3EFE9] pt-2">
              {accLabel(r.cash_account_code)} → {accLabel(r.category_account_code)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecurringManager;
